/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useContext, useEffect } from 'react';
import fileLoaderSvg from '../../../images/file_upload.svg';
import { useNavigate } from 'react-router-dom';
import { StoreContext } from '../../Store';
import ParentsModal from '../Modals/ParentsModal';
import { LoadUntilReady } from '../../Util/Loaders';
import { normaliseLoadJsonResponse } from '../../../utils/unifiedDataFile';
import { useUnifiedDataFileActions } from '../queryHooks/useUnifiedDataFile';
import { useQueryClient } from '@tanstack/react-query';
import { getBusinessPartnerStatus, getBusinessPartnerParent } from '../../../api/v2/businessPartner';

const FileLoader = () => {
  const [selectedFileType, setSelectedFileType] = useState();
  const [isFilePicked, setIsFilePicked] = useState(false);
  const store = useContext(StoreContext);
  const [selectedFile, setSelectedFile] = store.importFileData;
  const navigate = useNavigate();
  const [requestFailed, setRequestFailed] = useState(false);
  const { uploadUnifiedDataFileMutation } = useUnifiedDataFileActions();
  const loading = uploadUnifiedDataFileMutation.isPending;
  const [showParentsModal, setShowParentsModal] = useState(false);
  const [parents, setParents] = useState([]);
  const [selectedParent, setSelectedParent] = useState(null);
  const [clientIdValue, setClientIdValue] = useState(null);

  const queryClient = useQueryClient();

  const handleChange = (e) => {
    const file = e.target.files[0];
    setSelectedFile(file);
    setIsFilePicked(true);
    const name = file.name.toLowerCase();

    let fileType;
    if (name.endsWith('.xlsx')) {
      fileType = 'xls';
    } else if (name.endsWith('.json')) {
      fileType = 'json';
    } else {
      fileType = name.slice(-3);
    }
    setSelectedFileType(fileType);
  };

  useEffect(() => {
    store.infoText[1](
      'You can submit your New Load files using our file uploader. Before pressing the final submit button, please verify the file details summary section (displayed upon file selection) are correct.  You will also need to ensure that the file type chosen is currently supported by this upload facility. Currently, file types accepted are: CSV, XLS, XML and JSON (CABIE file).'
    );
  }, [isFilePicked]);

  const handleParentSelect = (parentId) => {
    setSelectedParent(parentId);
  };

  const handleContinue = async () => {
    if (selectedParent) {
      const parent = parents.find((p) => p.ParentTraderId === selectedParent);
      if (parent) {
        await store.updateParentAndLoadDefaults({
          parentId: parent.ParentTraderId,
          parentName: parent.companyName,
          parentEori: parent.eori,
        });
      }
    }
    navigate('/load');
  };

  const handleSubmit = async () => {
    const formData = new FormData();
    formData.append('file', selectedFile);
    try {
      let data = await uploadUnifiedDataFileMutation.mutateAsync({
        fileType: selectedFileType,
        formData,
      });
      if (selectedFileType === 'json') {
        data = normaliseLoadJsonResponse(data);

        // if load has no valid parent, then clear the customer ID to force selecting a new customer
        if (!data.Headers[0].traderParent) {
          data.Headers[0].clientID = null;
        }
      }
      store.newLoadFileData[1](data);
      store.newLoadFromFile[1](true);

      //reset IPAFFS data
      store.ipaffsCategories[1]({});
      store.certificateCount[1](0);

      // if customer specified in RPA, we need to validate it's data
      let customerIsValid = true;
      const id = data.Headers[0].clientID;
      if (id) {
        const customerData = await queryClient.fetchQuery({
          queryKey: ['getBusinessPartnerStatus', id],
          queryFn: () => getBusinessPartnerStatus(id),
        });
        // for Build/Import your load, a customer is considered valid on these three conditions:
        customerIsValid =
          customerData.cabieStatusValid && customerData.dbStatusValid && customerData.billableCustomerValid;

        if (!customerIsValid) {
          navigate(`/load/customerValidation/${data.Headers[0].clientID}?from=fileLoader`);
          return;
        }

        // Handle parent selection logic
        if (customerData?.BPType == 'PARENT') {
          await store.updateParentAndLoadDefaults({
            parentId: customerData.id,
            parentName: customerData.companyName,
          });
          navigate('/load');
        } else {
          const businessPartnerId = customerData?.id;
          if (businessPartnerId) {
            let parentsList = await queryClient.fetchQuery({
              queryKey: ['getBusinessPartnerParent', businessPartnerId],
              queryFn: () => getBusinessPartnerParent(businessPartnerId),
            });

            parentsList = parentsList.filter((parent) => parent.checked);

            if (data.Headers[0].traderParent) {
              const findParent = parentsList.filter(
                (parent) => Number.parseInt(parent.ParentTraderId) === Number.parseInt(data.Headers[0].traderParent)
              );

              if (findParent.length > 0) {
                parentsList = findParent;
              }
            }

            if (parentsList.length === 1) {
              await store.updateParentAndLoadDefaults({
                parentId: parentsList[0].ParentTraderId,
                parentName: parentsList[0].companyName,
                parentEori: parentsList[0].eori,
              });
              navigate('/load');
            } else if (parentsList.length > 1) {
              setParents(parentsList);
              setClientIdValue(data.Headers[0].clientID);
              setShowParentsModal(true);
            } else {
              navigate('/load');
            }
          } else {
            navigate('/load');
          }
        }
      } else {
        navigate('/load');
      }
    } catch (err) {
      store.infoText[1]('We could not process your file, please review the file and try again.');
      setRequestFailed(true);
      store.newLoadFromFile[1](false);
    }
  };

  return (
    <>
      <ParentsModal
        showModal={showParentsModal}
        setShowModal={setShowParentsModal}
        onContinue={handleContinue}
        parents={parents}
        selectedParent={selectedParent}
        onParentSelect={handleParentSelect}
        value={clientIdValue}
      />
      {isFilePicked ? (
        <div>
          <LoadUntilReady loading={loading} loadingText="Please wait..." style={{ marginTop: '6rem' }}>
            <p className="section-title">File name</p>
            <p className="text-break">{selectedFile.name}</p>
            <p className="section-title">File type</p>
            <p>{selectedFileType}</p>
            <p className="section-title">File size (bytes)</p>
            <p>{selectedFile.size}</p>
            <p className="section-title">File last modified</p>
            {selectedFile?.lastModifiedDate?.toLocaleDateString() ?? 'N/A'}
            <div>
              {!requestFailed && (
                <button type="button" className="blue-button-newload file-loader-btn-submit" onClick={handleSubmit}>
                  Submit
                </button>
              )}
              {requestFailed && (
                <div style={{ marginTop: '1rem', fontSize: '0.85rem', color: 'rgb(255, 30, 30)' }}>
                  <button
                    type="button"
                    className="blue-button-newload file-loader-btn"
                    onClick={() => setIsFilePicked(false) & setRequestFailed(false)}
                  >
                    Back
                  </button>
                </div>
              )}
            </div>
          </LoadUntilReady>
        </div>
      ) : (
        <>
          <img src={fileLoaderSvg} height="100" alt="loader" />
          <label name="name" className="blue-button-newload file-loader-btn">
            RPA <input type="file" name="fileuploader" onChange={handleChange} accept=".csv,.xlsx,.xml,.json" />
          </label>
        </>
      )}
    </>
  );
};

export default FileLoader;
